import { toast } from "sonner";
import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight, Check } from "lucide-react";
import { getNote, scrubNote, normalizeScrub, reprocessNote, isNoteDone, recodeAndWait, getChangeLog, downloadChangeLogCsv, patientView, signNote, unsignNote } from "@/lib/noteClaim";
import { SignConfirm } from "@/components/createclaims/ccCells";
import { matchLabel } from "@/lib/createClaimsModel";
import ClaimFormV20 from "@/components/claim/ClaimFormV20";
import ClaimFormProgressive from "@/components/claim/ClaimFormProgressive";
import { ChangeLogModal, MatchPatientModal, UnsignConfirm } from "@/components/claim/ClaimWorkspaceDialogs";
import { patchNoteSoap } from "@/lib/dictation";
import { CwCtx } from "./cwContext";
import "./claimform-v20.css";

function SignBadge({ note, onSign, onUnsign, signing }) {
  const by = note.signed_by?.name || note.signed_by?.email || "";
  const at = note.signed_at ? new Date(note.signed_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";
  return note.signed ? (
    <span className="ws-sig group inline-flex items-center gap-1.5 text-[12.5px] h-[30px]" data-testid="ws-signed-badge">
      <Check size={14} strokeWidth={2.4} style={{ color: "#141412" }} />
      <b className="font-semibold" style={{ color: "#141412" }}>Signed</b>
      {by || at ? <span style={{ color: "#6E6E6B" }} title={note.signed_by?.email || undefined}>{[by, at].filter(Boolean).join(" · ")}</span> : null}
      {onUnsign ? <button type="button" onClick={onUnsign} className="ml-1 text-[12px] font-semibold rounded-[6px] px-1.5 h-[24px] opacity-0 group-hover:opacity-100 focus:opacity-100 hover:bg-[#F1F1F4] transition-opacity" style={{ color: "#6C6C69" }} data-testid="ws-unsign">Unsign</button> : null}
    </span>
  ) : (
    <span className="inline-flex items-center gap-2 text-[12.5px] h-[30px]" data-testid="ws-unsigned-badge">
      <span className="w-[7px] h-[7px] rounded-full" style={{ background: "#E0A030" }} />
      <b className="font-semibold" style={{ color: "#9A5A00" }}>Not signed</b>
      {onSign ? <button type="button" onClick={onSign} disabled={signing} data-testid="ws-sign"
        className="ml-1 text-[12px] font-semibold rounded-[8px] px-3 h-[28px] border bg-white hover:bg-[#F4F4F9] disabled:opacity-50" style={{ borderColor: "#E9E9EC", color: "#141412" }}>{signing ? "Signing…" : "Sign visit"}</button> : null}
    </span>
  );
}

const MRN_OK = new Set(["matched", "preselected"]);

// Full-screen claim workspace: reuses the exact Create-Claims claim form (ClaimFormV20)
// and carries the band queue (1 of N, ←/→, Esc, Back).
// embedded: rendered inside an app-shell claim tab (no portal, no ×); shortcuts only while that tab is active.
export default function ClaimWorkspace({ items, index, onIndex, backLabel, onClose, onChanged, embedded = false, active = true, tabKey = null, onEditing, initialRail = null }) {
  const [note, setNote] = useState(null);
  const [scrub, setScrub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const cur = items[index];
  const noteId = cur?.id;

  const runScrub = useCallback(async (isAlive = () => true) => {
    setScrub(null);
    try { const raw = await scrubNote(noteId); if (isAlive()) setScrub(normalizeScrub(raw)); }
    catch { if (isAlive()) setScrub({ passed: false, findings: [], errors: 0, warnings: 0, unavailable: true }); }
  }, [noteId]);

  useEffect(() => {
    if (!noteId) return undefined;
    let alive = true;
    setLoading(true); setError(false); setNote(null); setScrub(null);
    (async () => {
      try {
        // The list endpoint returns claim_readiness but GET /notes/{id} does not —
        // carry it over from the list item so the verdict rail has real data.
        const merge = (n) => (n ? { ...n, claim_readiness: n.claim_readiness || cur?.claim_readiness || null } : n);
        let n = merge(await getNote(noteId));
        if (!alive) return;
        if (!n) { setError(true); setLoading(false); return; }
        setNote(n); setLoading(false);
        // In-flight (Watch it code): poll until coded, live-updating the progressive fill.
        const t0 = Date.now();
        while (alive && !isNoteDone(n) && Date.now() - t0 < 180_000) {
          await new Promise((r) => setTimeout(r, 1200));
          if (!alive) return;
          n = merge(await getNote(noteId));
          if (n && alive) setNote(n);
        }
        if (alive && isNoteDone(n)) { window.dispatchEvent(new CustomEvent("revq:jobs-changed", { detail: { id: noteId, done: true } })); runScrub(() => alive); }
      } catch { if (alive) { setError(true); setLoading(false); } }
    })();
    return () => { alive = false; };
  }, [noteId, runScrub]);

  const inFlight = note && !isNoteDone(note);

  const step = useCallback((d) => { const ni = index + d; if (ni >= 0 && ni < items.length) onIndex(ni); }, [index, items.length, onIndex]);
  useEffect(() => {
    if (!active) return undefined;
    const h = (e) => {
      if (embedded && (e.target.closest?.("input,textarea,select,[contenteditable=true],[role=tablist]"))) return;
      if (e.key === "Escape") onClose(); else if (e.key === "ArrowRight") step(1); else if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [step, onClose, active, embedded]);

  const doReprocess = async () => { try { const n = await reprocessNote(noteId); if (n) setNote(n); await runScrub(); onChanged?.(); } catch { /* noop */ } };

  // §3.5 Recode from the edited note (use after a manual edit; contrast with reprocess from source).
  const [recoding, setRecoding] = useState(false);
  const [recodeErr, setRecodeErr] = useState(null);
  useEffect(() => { setRecodeErr(null); }, [noteId]);
  const keepReadiness = (n) => setNote((prev) => (n ? { ...n, claim_readiness: n.claim_readiness || prev?.claim_readiness || null } : prev));
  const doRecode = async () => {
    setRecoding(true); setRecodeErr(null);
    try {
      const { status, note: n, already } = await recodeAndWait(noteId, { timeoutMs: 120000, onNote: keepReadiness });
      if (already) toast("A recode was already running. revQ waited for it to finish.");
      if (status === "failed") { setRecodeErr(n?.recode_error || ""); toast.error("Recode didn't finish."); }
      else if (status === "busy") toast("The visit is still being processed, so it can't be recoded yet.");
      else if (status === "running") toast("The recode is still running. The codes update when it finishes.");
      else toast.success("Recoded from the note — review the codes before saving");
      await runScrub(); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start recode.");
    } finally { setRecoding(false); }
  };

  // §3.6 Visit change log
  const [clOpen, setClOpen] = useState(false); const [clData, setClData] = useState(null);
  const [clFilter, setClFilter] = useState("all");
  const openChangeLog = async (f = "all") => { setClFilter(f); setClOpen(true); setClData("loading"); try { setClData(await getChangeLog(noteId)); } catch { setClData([]); } };

  // §3.7 Match patient — only when the row still needs a match
  const [matchOpen, setMatchOpen] = useState(false);
  const needsMatch = note && patientView(note).badge !== "matched";
  const afterMatch = async () => { try { const n = await getNote(noteId); if (n) setNote(n); } catch {} onChanged?.(); };

  const visitId = note?.visit_id || items[index]?.visit_id || "";
  const mrn = note?.mrn || items[index]?.mrn || "";

  // Sign from the open claim — same confirm step as the Claims Queue row.
  const [signAsk, setSignAsk] = useState(null), [signing, setSigning] = useState(false);
  const doSign = async () => {
    setSigning(true);
    try {
      await signNote(noteId);
      keepReadiness(await getNote(noteId));
      toast.success("Visit signed");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Couldn't sign this visit."); }
    finally { setSigning(false); }
  };

  const [unsignAsk, setUnsignAsk] = useState(null);
  const unsign = async () => { await unsignNote(noteId); keepReadiness(await getNote(noteId)); onChanged?.(); };
  const doUnsign = async () => {
    try { await unsign(); toast.success("Visit unsigned"); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Couldn't unsign this visit."); }
  };
  const saveNote = async (body) => { await patchNoteSoap(noteId, body); keepReadiness(await getNote(noteId)); onChanged?.(); };
  const [head, setHead] = useState(null);
  const [cmd, setCmd] = useState(null);
  useEffect(() => { setHead(null); }, [noteId]);
  const cw = { recoding, recodeErr, doRecode, unsign, saveNote, setHead, openChangeLog, cmd, tabKey, setEditing: onEditing, initialRail };

  const body = (
    <CwCtx.Provider value={cw}>
    <div data-testid="claim-workspace" className="rvq-fade-in" style={embedded ? { position: "relative", height: "100%", background: "#fff", display: "flex", flexDirection: "column" } : { position: "fixed", inset: 0, zIndex: 9500, background: "#fff", display: "flex", flexDirection: "column" }}>
      <div className="ws-row1 flex items-center gap-3 px-5 py-[10px] min-h-[56px] border-b flex-shrink-0" style={{ borderColor: "#E9E9EC" }} data-testid="ws-row1">
        <div className="flex-1 min-w-0 flex items-baseline flex-wrap gap-x-3 gap-y-0.5">
          <button onClick={onClose} data-testid="ws-back" className="text-[13px] font-semibold text-[#141412] inline-flex items-center gap-0.5 whitespace-nowrap hover:opacity-70 self-center"><ChevronLeft size={16} />{backLabel || "Needs you today"}</button>
          <span className="w-px h-[18px] bg-[#E9E9EC] self-center" />
          {note ? <span className="text-[18px] font-bold text-[#141412] whitespace-nowrap" data-testid="ws-patient">{note.patient_name || "—"}</span> : null}
          {note ? (MRN_OK.has(String(note.match_status || "")) && mrn
            ? <span data-testid="ws-mrn" className="text-[12.5px] whitespace-nowrap" style={{ color: "#6E6E6B", fontFamily: "ui-monospace,Menlo,monospace" }}>MRN {mrn} · {matchLabel(note)}</span>
            : <span data-testid="ws-mrn" className="text-[12.5px] font-semibold whitespace-nowrap" style={{ color: "#C4302A" }}>No MRN · not matched</span>) : null}
          {note && !inFlight && head ? (
            <span className="text-[13px] basis-full lg:basis-auto" style={{ color: "#6E6E6B" }} data-testid="ws-meta">
              DOS {head.dos}{head.otherDos ? <span style={{ color: "#9A5A00", fontWeight: 600 }}> ({head.otherDos} line{head.otherDos === 1 ? "" : "s"} on another date)</span> : null} · POS {head.pos || "—"}{head.prov ? ` · ${head.prov}` : ""}{head.npi ? ` · NPI ${head.npi}` : ""}
              {head.dictation ? <> · <button type="button" className="hover:underline hover:text-[#141412]" onClick={() => setCmd({ type: "note", n: Date.now() })} data-testid="ws-meta-dictation">From a dictation session</button></> : null}
            </span>
          ) : null}
        </div>
        {note && !inFlight ? <button onClick={() => setCmd({ type: "source", n: Date.now() })} data-testid="cfv-view-source" className="flex-none text-[12px] font-semibold rounded-[8px] px-3 h-[30px] border bg-white hover:bg-[#F4F4F9] whitespace-nowrap" style={{ borderColor: "#E9E9EC", color: "#141412" }}>View source · evidence</button> : null}
        <div className="flex-none inline-flex items-center gap-1" data-testid="ws-pager">
          <button onClick={() => step(-1)} disabled={index === 0} data-testid="ws-prev" aria-label="Previous claim" className="w-[26px] h-[26px] grid place-items-center rounded-[7px] hover:bg-[#F4F4F9] disabled:opacity-35"><ChevronLeft size={16} /></button>
          <span className="text-[12.5px] font-semibold tabular-nums whitespace-nowrap" data-testid="ws-num">{index + 1} of {items.length}</span>
          <button onClick={() => step(1)} disabled={index >= items.length - 1} data-testid="ws-next" aria-label="Next claim" className="w-[26px] h-[26px] grid place-items-center rounded-[7px] hover:bg-[#F4F4F9] disabled:opacity-35"><ChevronRight size={16} /></button>
        </div>
        {embedded ? null : <button onClick={onClose} className="flex-none w-[30px] h-[30px] grid place-items-center rounded-[8px] text-[#6C6C69] hover:bg-[#F4F4F9] hover:text-[#141412]" aria-label="Close" data-testid="ws-close"><X size={18} /></button>}
      </div>

      {visitId || (note && !inFlight) ? (
        <div className="flex items-center gap-3 px-5 min-h-[44px] py-1 border-b flex-shrink-0 flex-wrap" style={{ borderColor: "#E9E9EC", background: "#fff" }} data-testid="ws-actions-bar">
          {visitId ? (
            <button onClick={() => { try { navigator.clipboard?.writeText(visitId); toast.success(`Copied ${visitId}`); } catch (e) { /* clipboard blocked */ } }}
              title="Visit ID · click to copy" data-testid="ws-visit-id" className="inline-flex items-baseline gap-1.5 rounded-[6px] px-1 -mx-1 hover:bg-[#F4F4F9]">
              <span className="text-[11px] font-semibold uppercase tracking-[.06em]" style={{ color: "#6E6E6B" }}>Visit ID</span>
              <span className="text-[13px] font-semibold" style={{ fontFamily: "ui-monospace,Menlo,monospace", color: "#141412" }}>{visitId}</span>
            </button>
          ) : null}
          {note && !inFlight ? (
            <>
              {note.signed ? <><span className="w-px h-[18px] bg-[#E9E9EC]" />
              <SignBadge note={note} signing={signing}
                onUnsign={(e) => setUnsignAsk({ rect: e.currentTarget.getBoundingClientRect() })} /></> : null}
              <span className="flex-1" />
              {needsMatch ? (
                <button onClick={() => setMatchOpen(true)} data-testid="ws-match-patient" className="text-[12px] font-bold rounded-[8px] px-3 h-[28px] inline-flex items-center gap-1.5" style={{ background: "#141412", color: "#fff" }}>Match patient</button>
              ) : null}
              {(() => {
                const ins = note?.insurance || {};
                const hasName = !!ins.payer_name;
                const matched = hasName && ins.payer_id && ins.payer_id !== "MISSING" && !ins.payer_id_missing && !ins.needs_review;
                const label = !hasName ? "Add insurance" : matched ? "Insurance" : "Match payer";
                const amber = !matched;
                return (
                  <button onClick={() => setCmd({ type: "payer", n: Date.now() })} data-testid="ws-manage-insurance"
                    className="text-[12px] font-semibold rounded-[8px] px-3 h-[28px] border bg-white hover:bg-[#F4F4F9] inline-flex items-center gap-1.5"
                    style={{ borderColor: amber ? "#F0D9A8" : "#E9E9EC", color: "#141412" }}>
                    {amber ? <span className="w-[7px] h-[7px] rounded-full flex-none" style={{ background: "#E0A030" }} data-testid="ws-ins-dot" /> : null}
                    {label}
                  </button>
                );
              })()}
              {recoding ? <span className="text-[12px]" style={{ color: "#6E6E6B" }} data-testid="ws-recoding">Recoding from the note…</span>
                : note.codes_stale ? (
                  <>
                    <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold" style={{ color: "#9A5A00" }} data-testid="ws-stale-banner"><span className="w-[7px] h-[7px] rounded-full" style={{ background: "#E0A030" }} />Codes out of date</span>
                    <button onClick={doRecode} data-testid="ws-recode" className="text-[12px] font-semibold rounded-[8px] px-3 h-[28px] border bg-white hover:bg-[#F4F4F9]" style={{ borderColor: "#E9E9EC", color: "#141412" }}>Recode</button>
                  </>
                ) : <button onClick={doRecode} data-testid="ws-recode" className="text-[12px] font-semibold rounded-[8px] px-2.5 h-[28px] hover:bg-[#F1F1F4]" style={{ color: "#6C6C69" }}>Recode from note</button>}
              <span className="w-px h-[18px] bg-[#E9E9EC]" />
              <button onClick={() => openChangeLog("all")} data-testid="ws-change-log" className="text-[12px] font-semibold rounded-[8px] px-3 h-[28px] border bg-white hover:bg-[#F4F4F9]" style={{ borderColor: "#E9E9EC", color: "#141412" }}>Change log</button>
            </>
          ) : null}
        </div>
      ) : null}
      {note && !inFlight && !note.signed ? (
        <div className="flex items-center gap-2 px-5 py-2 border-b flex-shrink-0 flex-wrap" style={{ background: "#FEF7EC", borderColor: "#F6DFC0" }} data-testid="ws-sign-band">
          <span className="w-[7px] h-[7px] rounded-full flex-none" style={{ background: "#FF9500" }} />
          <span className="text-[12.5px] font-semibold" style={{ color: "#9A5A00" }}>Sign this visit to save the claim.</span>
          <button type="button" disabled={signing} onClick={(e) => setSignAsk({ kind: "sign", n: note, rect: e.currentTarget.getBoundingClientRect() })} data-testid="ws-sign"
            className="text-[12px] font-semibold rounded-[7px] px-2.5 h-[26px] text-white hover:opacity-90 disabled:opacity-50" style={{ background: "#141412" }}>{signing ? "Signing…" : "Sign encounter"}</button>
        </div>
      ) : null}

      <div style={{ flex: 1, minHeight: 0, overflow: "hidden", background: "#fff" }}>
        {loading ? (
          <div className="h-full flex items-center justify-center" data-testid="ws-loading">
            <div className="revq-spinner" style={{ borderTopColor: "#141412", borderColor: "rgba(20,20,18,.18)" }} />
          </div>
        ) : error ? (
          <div className="h-full flex flex-col items-center justify-center gap-1"><div className="text-[13px] font-bold text-[var(--ink)]">Couldn't load this claim</div><div className="text-[11px] text-[var(--gray2)]">The claim detail isn't available yet.</div></div>
        ) : inFlight ? (
          <ClaimFormProgressive note={note} onBackground={() => { window.dispatchEvent(new CustomEvent("revq:background-job", { detail: { id: noteId, filename: note?.filename } })); onClose(); }} />
        ) : (
          <ClaimFormV20
            note={note} scrub={scrub} onRetryScrub={() => runScrub()}
            onScanNext={() => step(1)} onClose={onClose} onAccept={doReprocess}
          />
        )}
      </div>
      {clOpen ? <ChangeLogModal data={clData} visitId={visitId} filter={clFilter} onFilter={setClFilter} onCsv={() => downloadChangeLogCsv(noteId, visitId)} onClose={() => setClOpen(false)} /> : null}
      {matchOpen ? <MatchPatientModal note={note} onDone={afterMatch} onClose={() => setMatchOpen(false)} /> : null}
      {unsignAsk ? <UnsignConfirm rect={unsignAsk.rect} onCancel={() => setUnsignAsk(null)} onConfirm={() => { setUnsignAsk(null); doUnsign(); }} /> : null}
      {signAsk ? <SignConfirm c={signAsk} z={9700} onCancel={() => setSignAsk(null)} onConfirm={() => { setSignAsk(null); doSign(); }} /> : null}
    </div>
    </CwCtx.Provider>
  );
  return embedded ? body : createPortal(body, document.body);
}
